"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { capabilities } from "@/lib/data/capabilities";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "@/components/icons";
import { BrandMark, BrandMarks } from "@/components/brand-marks";

/** Below this many pixels a pointer gesture is a click, not a drag. */
const DRAG_THRESHOLD = 5;
/** Pause between page advances. Long enough to read a row of cards. */
const AUTO_MS = 5600;

function pageCountFor(track: HTMLElement) {
  const cards = [...track.querySelectorAll<HTMLElement>("[data-card]")];
  if (cards.length < 2) return 1;
  const gap = Number.parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 16;
  const step = cards[0].offsetWidth + gap;
  if (step <= 0) return 1;
  const visible = Math.max(1, Math.round((track.clientWidth + gap) / step));
  return Math.max(1, cards.length - visible + 1);
}

export function CapabilityRail() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [dragging, setDragging] = useState(false);
  const pausedRef = useRef(false);
  const activeRef = useRef(0);
  const pageCountRef = useRef(1);

  /* Pointer-drag state is kept in a ref: it changes every frame and must
     not trigger re-renders while the user is moving. */
  const drag = useRef({
    active: false,
    moved: false,
    startX: 0,
    startScroll: 0,
    lastX: 0,
    lastT: 0,
    velocity: 0,
  });
  const inertia = useRef(0);

  const sync = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    setAtStart(track.scrollLeft <= 1);
    setAtEnd(track.scrollLeft >= max - 1);

    const cards = [...track.querySelectorAll<HTMLElement>("[data-card]")];
    const count = pageCountFor(track);
    setPageCount(count);
    pageCountRef.current = count;

    const origin = track.getBoundingClientRect().left;
    let closest = 0;
    let best = Infinity;
    cards.forEach((card, index) => {
      const delta = Math.abs(card.getBoundingClientRect().left - origin);
      if (delta < best) {
        best = delta;
        closest = index;
      }
    });
    const page = Math.min(closest, count - 1);
    setActive(page);
    activeRef.current = page;
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(sync);
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    sync();
    return () => {
      cancelAnimationFrame(frame);
      cancelAnimationFrame(inertia.current);
      track.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [sync]);

  const goTo = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelectorAll<HTMLElement>("[data-card]")[index];
    if (!card) return;
    const inset = Number.parseFloat(getComputedStyle(track).paddingInlineStart) || 0;
    track.scrollTo({ left: Math.max(0, card.offsetLeft - inset), behavior: "smooth" });
  };

  useEffect(() => {
    if (pageCount < 2) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timer = window.setInterval(() => {
      if (pausedRef.current || drag.current.active) return;
      const count = pageCountRef.current;
      goTo((activeRef.current + 1) % count);
    }, AUTO_MS);
    return () => window.clearInterval(timer);
  }, [pageCount]);

  const overflow = pageCount > 1 && !(atStart && atEnd);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    // Touch already scrolls natively; only take over for mouse/pen.
    if (event.pointerType === "touch") return;
    const track = trackRef.current;
    if (!track) return;
    cancelAnimationFrame(inertia.current);
    drag.current = {
      active: true,
      moved: false,
      startX: event.clientX,
      startScroll: track.scrollLeft,
      lastX: event.clientX,
      lastT: event.timeStamp,
      velocity: 0,
    };
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    const track = trackRef.current;
    if (!d.active || !track) return;
    const dx = event.clientX - d.startX;

    if (!d.moved && Math.abs(dx) > DRAG_THRESHOLD) {
      d.moved = true;
      setDragging(true);
      // Snap fights a free drag, so it is suspended for the gesture.
      track.style.scrollSnapType = "none";
      track.setPointerCapture(event.pointerId);
    }
    if (!d.moved) return;

    const dt = event.timeStamp - d.lastT;
    if (dt > 0) d.velocity = (event.clientX - d.lastX) / dt;
    d.lastX = event.clientX;
    d.lastT = event.timeStamp;
    track.scrollLeft = d.startScroll - dx;
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    const track = trackRef.current;
    if (!d.active || !track) return;
    d.active = false;
    if (!d.moved) return;

    if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
    setDragging(false);

    // Carry the gesture forward, then hand control back to scroll-snap.
    let velocity = d.velocity * 16;
    const glide = () => {
      velocity *= 0.94;
      track.scrollLeft -= velocity;
      if (Math.abs(velocity) > 0.4) {
        inertia.current = requestAnimationFrame(glide);
      } else {
        track.style.scrollSnapType = "";
      }
    };
    if (Math.abs(velocity) > 1) glide();
    else track.style.scrollSnapType = "";
  };

  /* A drag that ends on a card would otherwise fire that card's link. */
  const onClickCapture = (event: React.MouseEvent) => {
    if (drag.current.moved) {
      event.preventDefault();
      event.stopPropagation();
      drag.current.moved = false;
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    event.preventDefault();
    goTo(Math.min(pageCount - 1, Math.max(0, active + (event.key === "ArrowRight" ? 1 : -1))));
  };

  return (
    <div
      className={[
        "capability-rail",
        atStart ? "at-start" : "",
        atEnd ? "at-end" : "",
        dragging ? "is-dragging" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onPointerEnter={() => {
        pausedRef.current = true;
      }}
      onPointerLeave={() => {
        pausedRef.current = false;
      }}
      onFocusCapture={() => {
        pausedRef.current = true;
      }}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          pausedRef.current = false;
        }
      }}
    >
      <div className="capability-head">
        <div className="capability-head-copy">
          <p>$100M+ in Google Ads spend managed</p>
          <small>Five ways we turn marketing activity into measurable growth.</small>
        </div>
        {overflow ? (
          <div className="capability-nav">
            <button
              type="button"
              aria-label="Previous capability"
              disabled={atStart}
              onClick={() => goTo(Math.max(0, active - 1))}
            >
              <ArrowLeft />
            </button>
            <button
              type="button"
              aria-label="Next capability"
              disabled={atEnd}
              onClick={() => goTo(Math.min(pageCount - 1, active + 1))}
            >
              <ArrowRight />
            </button>
          </div>
        ) : null}
      </div>

      <div
        className="capability-track"
        ref={trackRef}
        role="group"
        aria-label="What we do"
        tabIndex={overflow ? 0 : undefined}
        onKeyDown={overflow ? onKeyDown : undefined}
        onPointerDown={overflow ? onPointerDown : undefined}
        onPointerMove={overflow ? onPointerMove : undefined}
        onPointerUp={overflow ? endDrag : undefined}
        onPointerCancel={overflow ? endDrag : undefined}
        onClickCapture={overflow ? onClickCapture : undefined}
      >
        {capabilities.map((item) => (
          <Link
            key={item.href}
            href={item.href}
            className="capability-card"
            data-card
            draggable={false}
          >
            {item.marks?.length === 1 ? (
              <span className="service-icon capability-icon brand-mark-solo" aria-hidden="true">
                <BrandMark id={item.marks[0]} size={20} />
              </span>
            ) : item.marks?.length ? (
              <span className="capability-mark-grid" aria-hidden="true">
                <BrandMarks ids={item.marks.slice(0, 5)} size={13} />
              </span>
            ) : (
              <span className={`service-icon capability-icon icon-${item.icon}`} aria-hidden="true">
                <span />
              </span>
            )}
            <span className="capability-body">
              <span className="capability-kicker">{item.kicker}</span>
              <strong>{item.title}</strong>
              <small>{item.detail}</small>
            </span>
            <ArrowUpRight className="capability-arrow" />
          </Link>
        ))}
      </div>

      {overflow ? (
        <div className="capability-dots" role="tablist" aria-label="Capability pages">
          {Array.from({ length: pageCount }, (_, index) => (
            <button
              key={index}
              type="button"
              role="tab"
              aria-selected={index === active}
              aria-label={`Show set ${index + 1} of ${pageCount}`}
              className={index === active ? "is-active" : undefined}
              onClick={() => goTo(index)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
